// src/oura/components/visibility/QuestionDrawer.tsx
//
// One question, opened: what each engine said, whether it named the business,
// who it named instead, and the ChatGPT page as the app showed it. This is the
// evidence behind every cell in the table, and the merchant's way to check a
// claim on their own phone.
//
// Read top to bottom as three answers to three questions (Jack, 20 Oct 2026):
// did the AI name us (the verdict card, whose engine tiles are also the tabs),
// what exactly did it say (the answer, its sources and the screenshot behind
// it), and who else did it name. One card each, nothing said twice.

import { useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { DELIVERY_ON } from "../../delivery/flag";
import { CheckIcon, ChevronLeftIcon, CopyIcon, MinusIcon, PencilIcon, RefreshCwIcon, SmartphoneIcon } from "lucide-react";
import type { AeoQuestionAnswer } from "@service/aeo";
import { Drawer, DrawerBlock, EngineLogo, FOCUS_RING, PillButton, SURFACE, TYPE } from "@oura/ui";
import { cn } from "../../lib/cn";
import { shortDate } from "../../lib/oura";
import { VISIBILITY_CHECK_LABEL } from "../../lib/visibilityChecks";
import { searchTierLabel } from "../../topSearches";
import { AnswerMarkdown, type AnswerHighlight } from "../aeo/AnswerMarkdown";
import { AnswerScreenshot, CaptureView } from "../aeo/AnswerScreenshot";
import { AnswerSkeleton, JudgementNote, SourceList } from "../aeo/QuestionAnswers";
import {
    allRivals,
    openEngineTab,
    rivalMentions,
    VIS_ENGINES,
    type EngineFilter,
    type VisCell,
    type VisEngine,
    type VisModel,
    type VisRow,
} from "../../visibility/model";
import type { AnswerLoad, QuestionAnswers } from "../../visibility/useQuestionAnswers";
import { SiteStatus } from "./cells";
import { FixWording, FixedNotice } from "./FixWording";
import { VerdictFixForm, VerdictFixNote, type VerdictFixTarget } from "./VerdictFix";
import type { AeoVerdictCorrection } from "@service/aeo";
import { questionRowControl } from "./rowFocus";

/** How each engine is asked, for staff beside its answer. */
function instrumentNote(model: VisModel, engine: VisEngine, row: VisRow): string | null {
    if (engine === "chatgpt") return model.engines.chatgpt.instrument ?? row.excerpts.chatgpt?.instrument ?? null;
    if (engine === "gemini") return "API, no location";
    return model.engines.ai_overview.instrument;
}

/**
 * What an empty answer says, per engine (#4180). Google not showing an AI
 * Overview for a search is a result, not a failure; ChatGPT and Gemini with
 * nothing stored is the case the default sentence is for.
 */
function emptyTextFor(engine: VisEngine): string | undefined {
    return engine === "ai_overview" ? "Google showed no AI answer for this search." : undefined;
}

/** The position the answer itself gave the business, when it numbered one. */
function rankOf(load: AnswerLoad | undefined, engine: VisEngine): number | null {
    const full = load?.kind === "ready" ? load.byEngine[engine] : undefined;
    return typeof full?.rank === "number" && full.rank > 0 ? full.rank : null;
}

const ordinal = (n: number) => {
    const s = n % 100 >= 11 && n % 100 <= 13 ? "th" : ({ 1: "st", 2: "nd", 3: "rd" } as Record<number, string>)[n % 10] ?? "th";
    return `${n}${s}`;
};

type Verdict = { word: string; tone: "named" | "not" | "none" };

/**
 * One engine's result in a word or two, for its tile. "Not named" and "not
 * measured" stay apart: an engine that was not asked is not a miss.
 */
function verdictFor(cell: VisCell | null, engine: VisEngine, model: VisModel, rank: number | null): Verdict {
    if (engine === "ai_overview" && !model.engines.ai_overview.perQuestion) return { word: "Scan total only", tone: "none" };
    if (!cell) return { word: model.engines[engine].named === null ? "Not asked" : "Not measured", tone: "none" };
    if (cell.unread) return { word: "Could not read", tone: "none" };
    if (!cell.named) return { word: "Not named", tone: "not" };
    if (cell.samples > 1) return { word: `Named ${cell.hits} of ${cell.samples}`, tone: "named" };
    return { word: rank !== null ? `Named ${ordinal(rank)}` : "Named", tone: "named" };
}

/** The verdict card's sentence: how many of the engines that answered named the business. */
function headline(row: VisRow): { text: string; named: boolean | null } {
    const counted = VIS_ENGINES.filter((e) => row.cells[e] && !row.cells[e]?.unread);
    if (counted.length === 0) return { text: "Not measured on this scan", named: null };
    const named = counted.filter((e) => row.cells[e]?.named).length;
    if (named === 0) return { text: counted.length === 1 ? "Not named" : "Not named by any of them", named: false };
    if (named === counted.length) return { text: counted.length === 1 ? "Named" : counted.length === 2 ? "Named by both" : `Named by all ${counted.length}`, named: true };
    return { text: `Named by ${named} of ${counted.length}`, named: true };
}

/**
 * The verdict, and the engine picker in one (#4025 FE-8 kept: one answer at a
 * time). The tiles were a "Named" fact tile AND a tab strip that each showed
 * the same three marks; a tile per engine that says its result and opens its
 * answer says it once.
 */
function VerdictCard({
    row,
    model,
    load,
    value,
    onChange,
    panelId,
    fixes,
    onCorrect,
}: {
    row: VisRow;
    model: VisModel;
    load: AnswerLoad | undefined;
    value: VisEngine;
    onChange: (e: VisEngine) => void;
    panelId: string;
    /** Staff verdict corrections on this question, per engine. */
    fixes?: Partial<Record<VisEngine, AeoVerdictCorrection>>;
    /** Staff: open the correction for the engine on show. Absent where it cannot be made. */
    onCorrect?: () => void;
}) {
    const tiles = useRef<Array<HTMLButtonElement | null>>([]);
    const head = headline(row);
    const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>, i: number) => {
        if (e.key !== "ArrowRight" && e.key !== "ArrowLeft") return;
        e.preventDefault();
        const next = (i + (e.key === "ArrowRight" ? 1 : VIS_ENGINES.length - 1)) % VIS_ENGINES.length;
        tiles.current[next]?.focus();
        onChange(VIS_ENGINES[next]);
    };
    return (
        <section className={cn(SURFACE.block, "p-5")}>
            <div className="flex items-start gap-3">
                <span
                    aria-hidden
                    className={cn(
                        "mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-full",
                        head.named === true ? "bg-emerald-500 text-white" : head.named === false ? "bg-gray-200 text-gray-500" : "bg-gray-100 text-gray-400",
                    )}
                >
                    {head.named === true ? <CheckIcon size={15} strokeWidth={3} /> : <MinusIcon size={15} strokeWidth={3} />}
                </span>
                <div className="min-w-0">
                    <p className={cn(TYPE.tt, "m-0 text-gray-900")}>{head.text}</p>
                    <p className={cn(TYPE.tc, "m-0 mt-0.5 flex flex-wrap items-center gap-1.5 text-gray-500")}>
                        Your site
                        <SiteStatus status={row.site} />
                    </p>
                </div>
            </div>
            <div role="tablist" aria-label="Engine" className="mt-4 grid grid-cols-3 gap-1 rounded-[14px] bg-gray-100 p-1">
                {VIS_ENGINES.map((e, i) => {
                    const on = e === value;
                    const fix = fixes?.[e];
                    const read = verdictFor(row.cells[e], e, model, rankOf(load, e));
                    // A sample keeps its cells: the correction is shown on the tile. Live, the recount already moved the cell.
                    const v: Verdict = fix ? { word: `${fix.named ? "Named" : "Not named"} · corrected`, tone: fix.named ? "named" : "not" } : read;
                    return (
                        <button
                            key={e}
                            ref={(el) => { tiles.current[i] = el; }}
                            type="button"
                            role="tab"
                            id={`${panelId}-tab-${e}`}
                            aria-controls={panelId}
                            aria-selected={on}
                            tabIndex={on ? undefined : -1}
                            onClick={() => onChange(e)}
                            onKeyDown={(ev) => onKeyDown(ev, i)}
                            className={cn(
                                "min-w-0 rounded-[10px] px-3 py-2.5 text-left transition-[background-color,box-shadow] duration-200",
                                on ? "bg-white shadow-[0_1px_3px_rgba(0,0,0,0.08),0_0_0_1px_rgba(0,0,0,0.04)]" : "hover:bg-white/60",
                                "focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-blue-600",
                            )}
                        >
                            <span className="flex min-w-0 items-center gap-1.5">
                                <EngineLogo engine={e} size={15} />
                                <span className={cn(TYPE.tc, "truncate font-medium", on ? "text-gray-900" : "text-gray-600")}>{model.engines[e].label}</span>
                            </span>
                            <span
                                className={cn(
                                    "mt-1 flex items-center gap-1 text-[13px] font-semibold leading-[18px]",
                                    v.tone === "named" ? "text-emerald-700" : v.tone === "not" ? "text-gray-500" : "font-medium text-gray-400",
                                )}
                            >
                                {v.tone === "named" && <CheckIcon aria-hidden size={13} strokeWidth={3} />}
                                <span className="truncate">{v.word}</span>
                            </span>
                        </button>
                    );
                })}
            </div>
            {onCorrect && (
                <p className={cn(TYPE.tc, "m-0 mt-3 text-gray-500")}>
                    {model.engines[value].label}&apos;s verdict looks wrong?{" "}
                    <button type="button" onClick={onCorrect} className={cn("rounded font-medium text-blue-600 hover:underline", FOCUS_RING)}>Correct it</button>
                </p>
            )}
        </section>
    );
}

/** The answer as the answer store holds it: the full text and its evidence. */
function FullAnswer({
    answer,
    engine,
    highlight,
}: {
    answer: AeoQuestionAnswer;
    engine: VisEngine;
    highlight: AnswerHighlight;
}) {
    return (
        <>
            {/* Who ranked above, in the engine's own order: the competitive
                picture the verdict only summarises. */}
            {answer.competitors && answer.competitors.length > 0 && (
                <p className={cn(TYPE.tc, "m-0 mt-1 text-gray-500")}>
                    Ranked above you: {answer.competitors.join(", ")}
                </p>
            )}
            <AnswerMarkdown reading text={answer.answer ?? ""} highlight={highlight} emptyText={emptyTextFor(engine)} className="mt-3" />
            <JudgementNote dropped={answer.droppedReason} recovered={answer.recoveredAlias} />
        </>
    );
}

/** Below the answer, on a hairline: the pages it cited and the picture of it. */
function Evidence({ sources, capture }: { sources: string[]; capture: ReactNode }) {
    if (!sources.length && !capture) return null;
    return (
        <div className="mt-5 space-y-4 border-t border-gray-100 pt-4">
            <SourceList sources={sources} />
            {capture}
        </div>
    );
}

function EngineAnswer({
    engine,
    row,
    model,
    staff,
    load,
    chars,
    assessmentId,
    highlight,
    onRetry,
}: {
    engine: VisEngine;
    row: VisRow;
    model: VisModel;
    staff: boolean;
    load: AnswerLoad | undefined;
    chars: number | null;
    assessmentId?: string;
    highlight: AnswerHighlight;
    onRetry: () => void;
}) {
    const stat = model.engines[engine];
    const cell = row.cells[engine];
    const full = load?.kind === "ready" ? load.byEngine[engine] : undefined;
    const n = load?.kind === "ready" ? load.n : null;
    const excerpt = row.excerpts[engine];
    const capture = engine === "chatgpt" && !full ? model.captureFor?.(row.id) ?? null : null;
    const note = staff ? instrumentNote(model, engine, row) : null;
    const rank = rankOf(load, engine);
    /**
     * Why there is no position, when there is none (#4025 FE-9).
     *
     * Only ChatGPT ever carried a rank, so an engine with no chip looked like
     * an engine with no result. BE-4 computes one for every engine, and sends
     * `rank_available` to separate the two reasons it can still be absent:
     * the answer ranked businesses and did not include this one, or the answer
     * is prose and has no list to have a position in.
     *
     * Staff only. A merchant reading their own answers is not asking why a
     * chip is missing from a paragraph, and the line would be noise on every
     * prose answer -- which, on the live Lush Clinic scan, is most of them.
     * Absent `rank_available` says nothing at all: an older payload cannot
     * tell the two apart, and guessing would be inventing a finding.
     */
    const rankNote = !staff || rank !== null || full?.rank_available === undefined
        ? null
        : full.rank_available
            // 19 Oct 2026, Brickhouse: named under a heading is in the answer, just not numbered.
            ? cell?.named
                ? "Named in this answer, but not as its own numbered item."
                : "This answer ranked businesses and did not include this one."
            : "Not a ranked answer, so there is no position to read.";

    // `screenshotKey` says a capture EXISTS; `n` is its address. An API
    // answer has an `n` and no capture, so gating on `n` alone drew a
    // heading over a broken image.
    const shot = full
        ? engine === "chatgpt" && assessmentId && n !== null && full.screenshotKey != null
            ? <AnswerScreenshot compact assessmentId={assessmentId} n={n} takenAt={full.screenshotTakenAt} />
            : null
        : capture
            ? <CaptureView compact src={capture.src} takenAt={capture.takenAt} />
            : null;

    return (
        <section className={cn(SURFACE.block, "p-5")}>
            <div className="flex items-baseline gap-2">
                <h3 className={cn(TYPE.lab, "m-0")}>What {stat.label} said</h3>
                {note && <span className={cn(TYPE.tm, "ml-auto text-gray-400")}>{note}</span>}
            </div>
            {/* Its own line, not a chip among the chips: a sentence in that row
                reads as a chip that failed to render. */}
            {rankNote && <p className={cn(TYPE.tm, "m-0 mt-1 text-gray-400")}>{rankNote}</p>}
            {full ? (
                <>
                    <FullAnswer answer={full} engine={engine} highlight={highlight} />
                    <Evidence sources={full.sources ?? []} capture={shot} />
                    {/* Provenance, deliberately not a link: logged out, the URL opens the
                        ChatGPT homepage, which would look like fabricated evidence. */}
                    {staff && full.conversationUrl && full.conversationUrlViewable === false && (
                        <p className="m-0 mt-3 break-all text-xs text-gray-400">Conversation reference: {full.conversationUrl}</p>
                    )}
                </>
            ) : load?.kind === "loading" ? (
                <AnswerSkeleton chars={chars} />
            ) : excerpt ? (
                <>
                    <AnswerMarkdown reading text={excerpt.text} highlight={highlight} emptyText={emptyTextFor(engine)} className="mt-3" />
                    {excerpt.truncated && <p className={cn(TYPE.tm, "m-0 mt-2 text-gray-400")}>The first 600 characters, as the scan kept them.</p>}
                    {load?.kind === "error" && (
                        <p className={cn(TYPE.tm, "m-0 mt-2 text-gray-500")}>
                            The full answer did not load.{" "}
                            <button type="button" onClick={onRetry} className="font-medium text-blue-600 hover:underline">Try again</button>
                        </p>
                    )}
                    <Evidence sources={excerpt.citedPages} capture={shot} />
                </>
            ) : load?.kind === "error" ? (
                <p className={cn(TYPE.tb, "m-0 mt-3 flex flex-wrap items-center gap-2 text-gray-600")}>
                    We could not load this answer.
                    <PillButton size="sm" icon={RefreshCwIcon} onClick={onRetry}>Try again</PillButton>
                </p>
            ) : (
                // An engine the run asked that has nothing stored for this one
                // question is said plainly: an empty card under "Not named"
                // would read as a measured result, and it is not one.
                <p className={cn(TYPE.tb, "m-0 mt-3 text-gray-500")}>
                    {cell || stat.named !== null ? "No stored answer for this question on this scan." : "Not asked on this scan."}
                </p>
            )}
        </section>
    );
}

/** How many rivals show before "Show all": enough to read the field, short enough to scan. */
const RIVALS_SHOWN = 5;

const engineList = (engines: VisEngine[], model: VisModel) => {
    const words = engines.map((e) => model.engines[e].label);
    return words.length <= 1 ? words.join("") : `${words.slice(0, -1).join(", ")} and ${words[words.length - 1]}`;
};

/**
 * Every rival the answers named on this question, once each, with every
 * engine that named it. The ones named in the business's place (by an engine
 * that left it out) come first and say so, because those are the answers the
 * business lost; the rest were named beside it.
 */
function RivalsBlock({ row, model }: { row: VisRow; model: VisModel }) {
    const [all, setAll] = useState(false);
    const mentions = rivalMentions(row);
    if (mentions.length === 0) return null;
    const shown = all ? mentions : mentions.slice(0, RIVALS_SHOWN);
    return (
        <section className={cn(SURFACE.block, "p-5")}>
            <h3 className={cn(TYPE.lab, "m-0")}>Other businesses named</h3>
            <ul className="list-plain m-0 mt-2 p-0">
                {shown.map((m) => {
                    const by = VIS_ENGINES.filter((e) => m.instead.includes(e) || m.beside.includes(e));
                    return (
                        <li key={m.name} className="flex min-h-[48px] min-w-0 items-center gap-3 border-t border-gray-100 py-2 first:border-t-0">
                            <span className="min-w-0 flex-1">
                                <span className={cn(TYPE.tb, "block truncate font-medium text-gray-900")} title={m.name}>{m.name}</span>
                                {m.instead.length > 0 && (
                                    <span className={cn(TYPE.tm, "block text-amber-700")}>
                                        Named instead of you by {engineList(m.instead, model)}
                                    </span>
                                )}
                            </span>
                            <span className="inline-flex shrink-0 items-center gap-1.5">
                                {by.map((e) => (
                                    <span key={e} title={model.engines[e].label} className="inline-flex">
                                        <EngineLogo engine={e} size={14} />
                                        <span className="sr-only">{model.engines[e].label}</span>
                                    </span>
                                ))}
                            </span>
                        </li>
                    );
                })}
            </ul>
            {mentions.length > RIVALS_SHOWN && (
                <button
                    type="button"
                    onClick={() => setAll((v) => !v)}
                    className={cn(TYPE.tc, "mt-1 rounded font-medium text-blue-600 hover:underline", FOCUS_RING)}
                >
                    {all ? "Show fewer" : `Show ${mentions.length - RIVALS_SHOWN} more`}
                </button>
            )}
        </section>
    );
}

type CopyState = "idle" | "copied" | "failed";

/**
 * The scan's own "Get named elsewhere" block (H21, `results.namedElsewhere`):
 * only questions lost to other sites carry it, with each site's engines
 * (Gemini's real publishers included) and a kept press page when there is one.
 */
export interface NamedElsewhereBlock {
    basis: "questions";
    questions: Array<{
        question: string;
        topicId?: string | null;
        answeredBy?: string | null;
        sites: Array<{ host: string; engines: string[] }>;
        pressPage?: { deliverableKey: string; itemId: string; state: "todo" | "waiting" | "done" } | null;
    }>;
}

export function QuestionDrawer({
    row,
    model,
    staff,
    businessName,
    answers,
    assessmentId,
    backTo,
    engine: pageEngine,
    onClose,
    onFix,
    namedElsewhere = null,
    fixAssessmentId,
    onCorrected,
    verdictTarget,
}: {
    row: VisRow | null;
    model: VisModel;
    staff: boolean;
    /**
     * The engine the page is filtered to. The drawer opens on that engine's
     * answer, so a reader who narrowed the table to Gemini and clicked a row
     * is not dropped on ChatGPT (#4025 FE-8).
     */
    engine: EngineFilter;
    businessName: string;
    /** The scan's block when it has one; without it the drawer reads the answers' own citations. */
    namedElsewhere?: NamedElsewhereBlock | null;
    answers: QuestionAnswers;
    /** The scan on screen, whose answers and captures are read. */
    assessmentId?: string;
    /** The source this question was opened from; closing goes back to it. */
    backTo: string | null;
    onClose: () => void;
    onFix: (taskId: string) => void;
    /** Staff, latest check only: Fix wording corrects this question on it and asks it again. */
    fixAssessmentId?: string;
    /** Re-read the check after a fix landed. */
    onCorrected?: () => void | Promise<void>;
    /** Staff: where a verdict correction is saved (the latest check, or a sample). */
    verdictTarget?: VerdictFixTarget;
}) {
    const [copy, setCopy] = useState<CopyState>("idle");
    // Fix wording is per question: another question, or the renamed one after a fix, starts closed.
    const [fixing, setFixing] = useState(false);
    const [fixed, setFixed] = useState<{ id: number; was: string } | null>(null);
    useEffect(() => setFixing(false), [row?.key]);
    useEffect(() => setFixed((f) => (f && f.id === row?.id ? f : null)), [row?.id]);
    const canFix = staff && !!fixAssessmentId && !!row && !row.reasking;
    // Verdict corrections: the open form, and on a sample the corrections made here (live ones come back on the row).
    const [correcting, setCorrecting] = useState(false);
    const [sampleFixes, setSampleFixes] = useState<Record<string, Partial<Record<VisEngine, AeoVerdictCorrection | null>>>>({});
    useEffect(() => setCorrecting(false), [row?.key]);
    const timer = useRef<number | undefined>(undefined);
    useEffect(() => () => window.clearTimeout(timer.current), []);
    useEffect(() => setCopy("idle"), [row?.key]);

    /**
     * Which engine's answer is open.
     *
     * The three answers used to be stacked, so reading Google's AI meant
     * scrolling past two full answers, their sources and a screenshot (#4025
     * FE-8). One at a time, and the one that opens is the one the reader is
     * most likely to want: the engine the page is filtered to, or -- under All
     * engines -- the first that actually named the business, because a reader
     * opening a row they can see is Named is looking for the answer that
     * named them. Failing both, the first tab.
     *
     * `null` until a row is open, then set per row: the drawer is one
     * component across rows, so without the reset every question would inherit
     * the last one's tab.
     */
    const [tab, setTab] = useState<VisEngine | null>(null);
    const panelId = useId();

    const openTab: VisEngine | null = row ? tab ?? openEngineTab(row, pageEngine) : null;
    useEffect(() => setTab(null), [row?.key, pageEngine]);

    const entry = row ? answers.entryFor(row.key) : undefined;
    const load = row ? answers.stateFor(row.key) : undefined;
    const { load: fetchAnswer } = answers;
    // Fetched once per question, on open, and only when the index says the
    // store holds it: a request that can only 404 is not worth a spinner.
    useEffect(() => {
        if (row && assessmentId && entry && !load) fetchAnswer(row.text);
    }, [row, assessmentId, entry, load, fetchAnswer]);

    if (!row) return null;

    const doCopy = () => {
        const done = (s: CopyState) => {
            setCopy(s);
            window.clearTimeout(timer.current);
            timer.current = window.setTimeout(() => setCopy("idle"), 2000);
        };
        if (!navigator.clipboard?.writeText) { done("failed"); return; }
        navigator.clipboard.writeText(row.text).then(() => done("copied"), () => done("failed"));
    };
    const copyLabel = copy === "copied" ? "Copied" : copy === "failed" ? "Could not copy" : staff ? "Copy question" : "Copy, then ask it on your phone";

    const highlight: AnswerHighlight = { you: [businessName], rivals: allRivals(row), soft: true };
    const gai = model.engines.ai_overview;
    // `google_ai`, never a bare `google`: the store uses that as one of
    // Gemini's own labels, and /google/ would hand Gemini's length to the
    // Google block.
    const ENGINE_RE: Record<VisEngine, RegExp> = { chatgpt: /chatgpt|openai/, gemini: /gemini/, ai_overview: /google_(ai|serp)/ };
    const charsFor = (e: VisEngine) => entry?.engines.find((x) => ENGINE_RE[e].test(x.engine))?.chars ?? null;
    const shown = openTab ?? VIS_ENGINES[0];
    const local = sampleFixes[row.key] ?? {};
    const fixes: Partial<Record<VisEngine, AeoVerdictCorrection>> = {};
    for (const e of VIS_ENGINES) {
        const f = e in local ? local[e] : row.verdictFixes?.[e];
        if (f) fixes[e] = f;
    }
    const shownFix = fixes[shown];
    const shownCell = row.cells[shown];
    // Only a verdict Oura actually read can be corrected: not asked, unread or a scan-total Google's AI has none.
    const canCorrect = staff && !!verdictTarget && !row.reasking && !!shownCell && !shownCell.unread && !shownFix
        && (shown !== "ai_overview" || model.engines.ai_overview.perQuestion);
    const keepFix = (e: VisEngine, f: AeoVerdictCorrection | null) => {
        if (verdictTarget?.sample) setSampleFixes((m) => ({ ...m, [row.key]: { ...(m[row.key] ?? {}), [e]: f } }));
        else void onCorrected?.();
    };

    // The question's type and its Google demand as one quiet line under the
    // title, not two chips: they describe the question, they are not results.
    const top = row.searches[0];
    const meta = [
        row.kind ? VISIBILITY_CHECK_LABEL[row.kind] : null,
        top && typeof top.rank === "number" ? searchTierLabel(top.rank) : null,
    ].filter(Boolean).join(" · ");
    const searchDetail = row.searches.map((m) => `"${m.query}" · Google #${Math.round(m.position)}`).join("\n");

    return (
        <Drawer
            key={row.key}
            open
            onClose={onClose}
            // WIDER than the 600px default (#4025 FE-7). This drawer holds the
            // evidence: a full answer, its sources, a screenshot, and a GFM
            // table (FE-23). 720 keeps the answer at a reading measure (about
            // 80 characters a line at 15px) while a three-column table still
            // fits; the shell caps itself to the viewport, so a phone is
            // unaffected.
            width={720}
            // A row opened by pointer never held focus, so the drawer sends
            // it back to that row's own control.
            returnFocus={() => questionRowControl(row.id)}
            title={row.text}
            sub={(backTo || meta) ? (
                <span className="flex flex-col items-start gap-1">
                    {backTo && (
                        <button
                            type="button"
                            onClick={onClose}
                            className={cn(TYPE.tc, "inline-flex items-center gap-1 rounded font-medium text-blue-600 hover:underline", FOCUS_RING)}
                        >
                            <ChevronLeftIcon aria-hidden size={14} />
                            Back to {backTo}
                        </button>
                    )}
                    {meta && <span title={searchDetail || undefined} className={cn(TYPE.tc, "text-gray-500")}>{meta}</span>}
                </span>
            ) : undefined}
            foot={staff ? (
                <>
                    {row.fixTaskId && !DELIVERY_ON && (
                        <PillButton variant="primary" onClick={() => row.fixTaskId && onFix(row.fixTaskId)}>View fix</PillButton>
                    )}
                    {canFix && !fixing && <PillButton icon={PencilIcon} onClick={() => setFixing(true)}>Fix wording</PillButton>}
                    <PillButton icon={CopyIcon} onClick={doCopy} aria-live="polite">{copyLabel}</PillButton>
                </>
            ) : (
                <PillButton variant="primary" icon={SmartphoneIcon} onClick={doCopy} aria-live="polite">{copyLabel}</PillButton>
            )}
        >
            {fixing && canFix && fixAssessmentId && (
                <FixWording
                    assessmentId={fixAssessmentId}
                    n={row.id}
                    text={row.text}
                    onCancel={() => setFixing(false)}
                    onDone={async (was) => {
                        await onCorrected?.();
                        setFixing(false);
                        setFixed({ id: row.id, was });
                    }}
                />
            )}
            {fixed && fixed.id === row.id && !fixing && <FixedNotice was={fixed.was} />}
            <VerdictCard
                row={row}
                model={model}
                load={load}
                value={shown}
                onChange={(e) => { setTab(e); setCorrecting(false); }}
                panelId={panelId}
                fixes={fixes}
                onCorrect={canCorrect && !correcting ? () => setCorrecting(true) : undefined}
            />
            {correcting && canCorrect && verdictTarget && shownCell && (
                <VerdictFixForm
                    key={`${row.key}:${shown}`}
                    target={verdictTarget}
                    n={row.id}
                    engine={shown}
                    engineLabel={model.engines[shown].label}
                    readNamed={shownCell.named}
                    onCancel={() => setCorrecting(false)}
                    onSaved={(f) => { setCorrecting(false); keepFix(shown, f); }}
                />
            )}
            {staff && shownFix && (
                <VerdictFixNote
                    target={verdictTarget}
                    n={row.id}
                    engine={shown}
                    engineLabel={model.engines[shown].label}
                    fix={shownFix}
                    onUndone={() => keepFix(shown, null)}
                />
            )}
            {answers.index && !answers.index.stored && (
                <p className={cn(TYPE.tm, "m-0 text-gray-500")}>Full answers were not stored on this scan. These are the excerpts it kept.</p>
            )}
            {/* One engine at a time (#4025 FE-8). Every engine keeps a tile,
                including Google's AI when the scan stored it as a total
                rather than per question: a missing tile would read as an
                engine that was never asked, and the panel says which it is. */}
            <div id={panelId} role="tabpanel" aria-labelledby={`${panelId}-tab-${shown}`}>
            {shown !== "ai_overview" || gai.perQuestion ? (
                <EngineAnswer
                    key={shown}
                    engine={shown}
                    row={row}
                    model={model}
                    staff={staff}
                    // The full overview, same as the assistants get (#3947).
                    load={load}
                    chars={charsFor(shown)}
                    assessmentId={assessmentId}
                    highlight={highlight}
                    onRetry={() => fetchAnswer(row.text)}
                />
            ) : (
                // Google's AI is stored as one total per scan, not per
                // question. Said as that, never as a card with no answer in it.
                <DrawerBlock className="p-5">
                    <p className={cn(TYPE.lab, "m-0")}>What {gai.label} said</p>
                    <p className={cn(TYPE.tb, "m-0 mt-2 text-gray-600")}>
                        {gai.named !== null && gai.of !== null
                            ? `Read as a total on this scan, not per question: named in ${gai.named} of the ${gai.of} AI Overviews Google showed.`
                            : gai.skipped}
                    </p>
                </DrawerBlock>
            )}
            </div>
            <RivalsBlock row={row} model={model} />
            {DELIVERY_ON && <NamedElsewhere row={row} businessName={businessName} block={namedElsewhere} staff={staff} />}
            <p className={cn(TYPE.tm, "m-0 text-center text-gray-400")}>
                {staff
                    ? `Scan of ${shortDate(model.capturedAt)}. Screenshots come from the ChatGPT app run only.`
                    : `Checked ${shortDate(model.capturedAt)}.`}
            </p>
        </Drawer>
    );
}


/**
 * Get named elsewhere, per question (H21): the sites the AI cited when it
 * answered this question without the client. Read from the answers' own
 * citations, so it shows only what the scan collected. It used to be a
 * Fixes step per question; a kept press page under B1 replaces it.
 */
const ENGINE_WORD: Record<string, string> = { gemini: "Gemini", openai: "ChatGPT", chatgpt: "ChatGPT", perplexity: "Perplexity", claude: "Claude" };
const engineWords = (engines: string[]) => {
    const words = engines.map((e) => ENGINE_WORD[e.toLowerCase()] ?? e.charAt(0).toUpperCase() + e.slice(1));
    return words.length <= 1 ? words.join("") : `${words.slice(0, -1).join(", ")} and ${words[words.length - 1]}`;
};

function NamedElsewhere({ row, businessName, block, staff }: { row: VisRow; businessName: string; block: NamedElsewhereBlock | null; staff: boolean }) {
    if (row.named) return null;
    // The client reads it about themselves, without our plan's page codes.
    const who = staff ? `${businessName} gets` : "you get";
    // What the client can do with it: the listing is work we take on.
    const next = staff ? "" : " Ask us about getting listed there.";
    // The scan's block decides which questions carry it: a question lost for want of a
    // page gets its fix on the client's own site instead, so it shows nothing here.
    if (block) {
        // Matched on the row's join key (trimmed, lower case), the same key the backend joins on.
        const q = block.questions.find((x) => x.question.trim().toLowerCase() === row.key);
        if (!q || (q.sites.length === 0 && !q.pressPage)) return null;
        const slot = q.pressPage ? Number(q.pressPage.itemId.split(":slot-")[1]) || null : null;
        const code = q.pressPage ? q.pressPage.deliverableKey.split("-")[0] : null;
        return (
            <DrawerBlock label="Get named elsewhere">
                {q.pressPage && (
                    <p className="m-0 mb-2 font-medium text-gray-900">
                        {q.pressPage.state === "done"
                            ? `The press and media page is live${staff && slot ? `: page ${slot} of ${code}` : ""}.`
                            : `A press and media page is planned${staff && slot ? `: page ${slot} of ${code}` : ""}.`}
                    </p>
                )}
                {q.sites.length > 0 && (
                    <>
                        <p className="m-0 text-gray-600">
                            The AI cited {q.sites.length === 1 ? "this site" : "these sites"} for this question. Being listed there is how {who} named for it.{next}
                        </p>
                        <ul className="m-0 mt-2 list-plain space-y-1 p-0">
                            {q.sites.map((s) => (
                                <li key={s.host} className={cn(TYPE.tc, "flex items-center gap-2")}>
                                    <span className="min-w-0 flex-1 truncate font-medium text-gray-900">{s.host}</span>
                                    <span className="text-gray-500">cited by {engineWords(s.engines)}</span>
                                </li>
                            ))}
                        </ul>
                    </>
                )}
            </DrawerBlock>
        );
    }
    const own = businessName.toLowerCase().replace(/[^a-z0-9]/g, "");
    const hosts = new Map<string, number>();
    Object.values(row.excerpts).forEach((x) => {
        (x?.citedPages ?? []).forEach((url) => {
            let host = "";
            try {
                host = new URL(url).hostname.replace(/^www\./, "");
            } catch {
                return;
            }
            if (!host || host.replace(/[^a-z0-9]/g, "").includes(own)) return;
            // Gemini's grounding returns Google redirect links, never the site itself.
            if (/(^|\.)(google\.[a-z.]+|vertexaisearch\.cloud\.google\.com|googleusercontent\.com|gstatic\.com)$/.test(host)) return;
            hosts.set(host, (hosts.get(host) ?? 0) + 1);
        });
    });
    const top = Array.from(hosts.entries()).sort((a, b) => b[1] - a[1]).slice(0, 3);
    if (top.length === 0) return null;
    const rivals = row.rivals.slice(0, 2);
    return (
        <DrawerBlock label="Get named elsewhere">
            <p className="m-0 text-gray-600">
                The AI cited {top.length === 1 ? "this site" : "these sites"} for this question{rivals.length ? `, naming ${rivals.join(" and ")}` : ""}. Being listed there is how {who} named for it.{next}
            </p>
            <ul className="m-0 mt-2 list-plain space-y-1 p-0">
                {top.map(([host, n]) => (
                    <li key={host} className={cn(TYPE.tc, "flex items-center gap-2")}>
                        <span className="min-w-0 flex-1 truncate font-medium text-gray-900">{host}</span>
                        <span className="text-gray-500">cited {n === 1 ? "once" : `${n} times`}</span>
                    </li>
                ))}
            </ul>
        </DrawerBlock>
    );
}
